'use client'

import React from 'react'
import {
  Accordion,
  AccordionChevron,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  List,
  ListItem,
  ListItemAction,
  ListItemContent,
  ListItemText,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../'
import { Chevron } from '../../icons'
import { cn } from '../../utils'

/**
 * MobileNavigationMenu component that contains MobileNavigationMenuCategoryList and MobileNavigationMenuSubCategoryList components.
 */
const MobileNavigationMenu = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof Tabs>) => (
  <Tabs ref={ref} className={cn('w-full', className)} {...props}>
    {children}
  </Tabs>
)

MobileNavigationMenu.displayName = 'MobileNavigationMenu'

/**
 * MobileNavigationMenuCategoryList component that wraps MobileNavigationMenuCategoryItem components used for each Tab.
 */
const MobileNavigationMenuCategoryList = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsList>) => (
  <TabsList
    ref={ref}
    className={cn(
      'flex overflow-x-auto whitespace-nowrap',
      '[&::-webkit-scrollbar]:h-2',
      '[&::-webkit-scrollbar-thumb]:bg-gray-200',
      '[&::-webkit-scrollbar-thumb]:rounded',
      className,
    )}
    {...props}
  >
    {children}
  </TabsList>
)

MobileNavigationMenuCategoryList.displayName =
  'MobileNavigationMenuCategoryList'

/**
 * MobileNavigationMenuCategoryItem component used for each Tab in the first level of the menu tree.
 */
const MobileNavigationMenuCategoryItem = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsTrigger>) => (
  <TabsTrigger ref={ref} className={className} {...props}>
    {children}
  </TabsTrigger>
)

MobileNavigationMenuCategoryItem.displayName =
  'MobileNavigationMenuCategoryItem'

/**
 * MobileNavigationMenuSubCategoryList component that wraps MobileNavigationMenuSubCategoryItem components used for each Tab.
 */
const MobileNavigationMenuSubCategoryList = ({
  value,
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsContent>) => (
  <TabsContent value={value} className={cn('p-0', className)} {...props}>
    <Accordion type="multiple">
      <List>{children}</List>
    </Accordion>
  </TabsContent>
)

MobileNavigationMenuSubCategoryList.displayName =
  'MobileNavigationMenuSubCategoryList'

/**
 * MobileNavigationMenuSubCategoryItem component represents each group in the second level of the menu tree.
 */
const MobileNavigationMenuSubCategoryItem = ({
  value,
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionItem>) => (
  <AccordionItem
    as="li"
    key={value}
    value={value}
    ref={ref}
    className={cn('data-[state=open]:[&_button]:bg-gray-100', className)}
    data-testid={value}
    {...props}
  >
    {children}
  </AccordionItem>
)

MobileNavigationMenuSubCategoryItem.displayName =
  'MobileNavigationMenuSubCategoryItem'

/**
 * MobileNavigationMenuSubCategoryItemLabel component represents the label for second level of the menu tree.
 */
const MobileNavigationMenuSubCategoryItemLabel = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ListItem>) => (
  <AccordionTrigger className="w-full">
    <ListItem as="div" asChild className={cn('w-full', className)} {...props}>
      <ListItemContent>
        <ListItemText className="text-xs font-medium">{children}</ListItemText>
      </ListItemContent>
      <ListItemAction>
        <AccordionChevron />
      </ListItemAction>
    </ListItem>
  </AccordionTrigger>
)

MobileNavigationMenuSubCategoryItemLabel.displayName =
  'MobileNavigationMenuSubCategoryItemLabel'

/**
 * MobileNavigationMenuSubCategoryItemWrapper component wraps the MobileNavigationMenuSubCategoryItemLink components used for third level of the menu tree.
 */
const MobileNavigationMenuSubCategoryItemWrapper = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionContent>) => (
  <AccordionContent
    ref={ref}
    as={List}
    className={cn('px-4', className)}
    {...props}
  >
    {children}
  </AccordionContent>
)

MobileNavigationMenuSubCategoryItemWrapper.displayName =
  'MobileNavigationMenuSubCategoryItemWrapper'

/**
 * MobileNavigationMenuSubCategoryItemLink component represents each item for third level of the menu tree.
 */
const MobileNavigationMenuSubCategoryItemLink = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ListItem>) => {
  const childrenAsString = React.Children.toArray(children)
    .map((child) =>
      React.isValidElement(child) && child.props
        ? (child.props as { children: React.ReactNode }).children
        : child,
    )
    .join('')

  return (
    <ListItem
      as="a"
      key={childrenAsString}
      className={cn('w-full', className)}
      {...props}
    >
      <ListItemContent>
        <ListItemText className="text-xs font-normal">{children}</ListItemText>
      </ListItemContent>
      <ListItemAction>
        <Chevron className="h-[6px] w-[12px]" />
      </ListItemAction>
    </ListItem>
  )
}

MobileNavigationMenuSubCategoryItemLink.displayName =
  'MobileNavigationMenuSubCategoryItemLink'

export {
  MobileNavigationMenu,
  MobileNavigationMenuCategoryList,
  MobileNavigationMenuCategoryItem,
  MobileNavigationMenuSubCategoryList,
  MobileNavigationMenuSubCategoryItem,
  MobileNavigationMenuSubCategoryItemLabel,
  MobileNavigationMenuSubCategoryItemWrapper,
  MobileNavigationMenuSubCategoryItemLink,
}
